import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'
import type { ProductTileProps } from '@design-system/components/UI/UIProductTile.props'
import type { ProductSchema } from '@integration-layer/.nuxt/armani/product-schema'
import type { ProductTileType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'

/**
 * Composable for managing "Shop the Look" functionality
 *
 * Provides methods to fetch and transform products for recommendations and quick buy
 *
 * @returns {Object} Shop the look utilities
 * @property {Function} getProducts - Fetch products by Xgen ID or Algolia IDs
 * @property {Function} transformProductForQuickBuy - Transform product for quick buy view
 *
 * @example
 * const { getProducts, transformProductForQuickBuy } = useShopTheLook()
 */
export const useShopTheLook = () => {
  const { skuPrices, fetchSkuPrices } = useProductTileClPrices()
  const { normalizeProduct, transformToProductCardHorizontal } =
    useWrapperProductTile()
  const { recommendations } = useXgen()

  /**
   * Fetches products based on Xgen ID or Algolia IDs
   *
   * @param {string | string[]} xgenIdOrAlgoliaIds - Xgen ID or list of Algolia product IDs
   * @returns {Promise<ProductTileProps[]>} List of product tile properties
   *
   * @example
   * const products = await getProducts('xgen-id-123')
   * const products = await getProducts(['algolia-id-1', 'algolia-id-2'])
   */
  const getProducts = async (xgenIdOrAlgoliaIds: string | string[]) => {
    let algoliaProducts: ProductSchema[]
    let isXGenId = false
    if (typeof xgenIdOrAlgoliaIds === 'string') {
      isXGenId = true
      algoliaProducts = await recommendations({
        predictionID: xgenIdOrAlgoliaIds,
      })
    } else if (Array.isArray(xgenIdOrAlgoliaIds) && xgenIdOrAlgoliaIds.length) {
      const algoliaIndex = useAppConfig().currentAlgoliaIndex
      algoliaProducts = await $fetch(`/api/getProducts/${algoliaIndex}`, {
        query: {
          mfcList: xgenIdOrAlgoliaIds.join(','),
        },
      })
    } else {
      return []
    }

    const filteredAlgoliaProducts = algoliaProducts.filter(isNonNullable)

    const skuCodes = filteredAlgoliaProducts
      ?.map(algoliaProduct => algoliaProduct?.size?.[0]?.SKU)
      ?.filter((skuCode): skuCode is string => !!skuCode)

    await fetchSkuPrices(skuCodes)

    const productsDetails = filteredAlgoliaProducts
      .map(product => {
        const skuWithSize = product?.size?.[0].SKU

        const productWithPrices = {
          ...product,
          ...(skuWithSize ? { clPrices: skuPrices.value?.[skuWithSize] } : {}),
        }

        const normalizedProduct = normalizeProduct(productWithPrices)

        if (!normalizedProduct) return

        if (isXGenId) {
          normalizedProduct.linkToProduct = `${normalizedProduct.linkToProduct}?xse_prod_code=${normalizedProduct.sku}&xse=${xgenIdOrAlgoliaIds}`
        }

        const productTileProps: ProductTileProps = {
          product: normalizedProduct,
          onlyImg: false,
          previewSlide: 0,
        }

        return productTileProps
      })
      .filter(p => p !== undefined)

    return productsDetails
  }

  /**
   * Transforms a product tile to a quick buy product card format
   *
   * @param {ProductTileType} product - Product tile to transform
   * @returns {ProductCardHorizontalProps} Transformed product card properties
   *
   * @example
   * const quickBuyProduct = transformProductForQuickBuy(productTile)
   */
  const transformProductForQuickBuy = (
    product: ProductTileType
  ): ProductCardHorizontalProps => {
    return transformToProductCardHorizontal(product)
  }

  return { getProducts, transformProductForQuickBuy }
}
